import { Slice } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';

import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { purePmInEditorDirectStreamingPluginKey } from '../pluginKey';
import { updateStreamPositions, metaTransaction } from '../transforms';
import type { PurePmInEditorDirectStreamingPluginState } from '../types';
import { convertADFToFragment, fragmentIsHealthy } from '../utils/adfConversion';
import { clampPosition } from '../utils/positionValidation';

import { calculateListAdjustment } from './positionAdjustments';
import { mapStartPos, mapToPos } from './positionMapping';
import { calculateSliceOpenStartEndAdjustment } from './sliceAdjustments';

/**
 * Builds transaction for committing final AI content.
 * This is Phase 2 of the two-dispatch finalize pattern.
 * Should be called AFTER restore transaction has been dispatched.
 *
 * Note: This closely matches the original commitFinalResponse in useInEditorDirectStreaming.
 * The slice adjustment (openStart/openEnd) is applied to prevent unwanted node merging.
 */
export function buildCommitTransaction(state: EditorState): Transaction | null {
	const sessionState = aiSessionContextPluginKey.getState(state);
	if (!sessionState) {
		return null;
	}
	const { adfResponse, responseVersion } = sessionState;

	const streamingState = purePmInEditorDirectStreamingPluginKey.getState(state);
	const decorationPluginState = aiExperienceDecorationPluginKey.getState(state);
	const { positions } = decorationPluginState ?? {};

	if (!streamingState || !positions) {
		return null;
	}

	// After restore, the document is back to original state
	// Use the positions from streaming state (which were mapped through restore)
	const { startPos, userContentEndPos, originalUserContentSlice, aiContentSlice } = streamingState;

	if (startPos === undefined || userContentEndPos === undefined) {
		return buildCommitStateOnly(state, streamingState, responseVersion);
	}

	const finalFragment = convertADFToFragment(state.schema, adfResponse);

	// IMPORTANT: Prefer the slice adjusted during the update phase when it still matches the
	// final adfResponse. This preserves the existing behavior for edge cases like inserting
	// a heading at the start of a paragraph. If the final response changed after the last
	// stream update, rebuild the slice from the final adfResponse so we don't commit stale
	// streamed content.
	const canReuseStreamedSlice =
		!!aiContentSlice &&
		!!finalFragment &&
		fragmentIsHealthy(finalFragment) &&
		aiContentSlice.content.eq(finalFragment);

	let slice = canReuseStreamedSlice ? aiContentSlice : undefined;

	// Fallback: create new slice if aiContentSlice not available
	if (!slice) {
		const fragment = finalFragment;
		if (!fragment || !fragmentIsHealthy(fragment)) {
			return buildCommitStateOnly(state, streamingState, responseVersion);
		}

		slice = Slice.maxOpen(fragment);
		if (!slice.content.firstChild) {
			slice = Slice.empty;
		}
	}


	// Use positions from streaming state
	const docSize = state.doc.content.size;
	let workingStartPos = clampPosition(startPos, docSize);
	let workingUserContentEndPos = clampPosition(userContentEndPos, docSize);

	// List adjustment (matching original)
	const listAdjustment = calculateListAdjustment({
		state,
		fragment: slice.content,
		startPos: workingStartPos,
		userContentEndPos: workingUserContentEndPos,
	});
	if (listAdjustment.adjusted) {
		workingStartPos = listAdjustment.startPos;
		workingUserContentEndPos = listAdjustment.userContentEndPos;
	}

	let tr = state.tr;
	const streamTimeStamp = streamingState.streamTimeStamp;
	const safeEndPos = Math.max(workingStartPos, Math.min(workingUserContentEndPos, docSize));

	// Adjust slice openStart/openEnd to prevent unwanted node merging
	// (e.g. losing a panel wrapper when inserting before an existing node)
	slice = calculateSliceOpenStartEndAdjustment({
		state,
		slice,
		startPos: workingStartPos,
		toPos: safeEndPos,
	});

	// Apply final content - matching original commitFinalResponse logic
	try {
		const { paragraph } = tr.doc.type.schema.nodes;
		// If we have a lone paragraph - replaceRange will not replace with a paragraph
		const isSingleParagraph =
			slice.content.firstChild === slice.content.lastChild &&
			slice.content.firstChild?.type === paragraph;

		if (isSingleParagraph && workingStartPos < docSize) {
			const $from = tr.doc.resolve(workingStartPos);
			const isEmptyParagraph = $from.parent.type === paragraph && $from.parent.content.size === 0;

			if (isEmptyParagraph && slice.content.firstChild) {
				tr = tr.replaceRangeWith(workingStartPos, safeEndPos, slice.content.firstChild);
			} else {
				tr = tr.replaceRange(workingStartPos, safeEndPos, slice);
			}
		} else {
			tr = tr.replaceRange(workingStartPos, safeEndPos, slice);
		}

		tr = metaTransaction({ tr, isFinalStream: true, stackTime: streamTimeStamp });
	} catch {
		return buildCommitStateOnly(state, streamingState, responseVersion);
	}

	// Set selection at end of inserted content
	try {
		tr = tr.setSelection(
			TextSelection.create(tr.doc, Math.min(tr.selection.to, tr.doc.content.size)),
		);
	} catch {
		// Invalid position - keep default selection
	}

	// Skip history if content unchanged
	if (originalUserContentSlice) {
		try {
			const newDocSize = tr.doc.content.size;
			const finalStartPos = clampPosition(mapStartPos({ pos: startPos, tr }), newDocSize);
			const finalEndPos = clampPosition(mapToPos({ pos: userContentEndPos, tr }), newDocSize);
			const newSlice = tr.doc.slice(finalStartPos, finalEndPos);
			if (newSlice.eq(originalUserContentSlice)) {
				tr = tr.setMeta('addToHistory', false);
			}
		} catch {
			// Position mapping failed - add to history as normal
		}
	}

	// Map final positions for telepointer
	const finalDocSize = tr.doc.content.size;
	const mappedStartPos = clampPosition(mapStartPos({ pos: startPos, tr }), finalDocSize);
	const mappedToPos = clampPosition(mapToPos({ pos: userContentEndPos, tr }), finalDocSize);

	let telepointerPos = mappedToPos;
	try {
		tr.doc.nodesBetween(mappedStartPos, mappedToPos, (node, pos) => {
			if (node.isLeaf) {
				telepointerPos = pos + node.nodeSize;
			}
		});
		telepointerPos = Math.min(telepointerPos, mappedToPos);
	} catch {
		// Position out of bounds - use mapped end position
	}

	tr = updateStreamPositions({
		aiContentEndPos: mappedToPos,
		telepointerPos,
		isFinalStream: true,
		lastProcessedVersion: responseVersion,
	})(tr);

	return tr;
}

function buildCommitStateOnly(
	state: EditorState,
	streamingState: PurePmInEditorDirectStreamingPluginState,
	responseVersion: number,
): Transaction {
	let tr = state.tr;
	tr = updateStreamPositions({
		aiContentEndPos: streamingState.aiContentEndPos ?? streamingState.startPos ?? 0,
		telepointerPos: streamingState.telepointerPos ?? streamingState.startPos ?? 0,
		isFinalStream: true,
		lastProcessedVersion: responseVersion,
	})(tr);
	return tr;
}
